import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Heart, Building2, Sparkles } from "lucide-react";
import PlacementRequestForm from "@/components/PlacementRequestForm";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "facility" ? "facility" : "caregiver";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [includePlacement, setIncludePlacement] = useState(params.get("placement") === "1");
  const [placement, setPlacement] = useState({
    loved_one_name: "",
    situation: "",
    care_priority: "memory_care",
    preferred_area: "",
    timeline: "1_3_months",
    preferred_contact: "phone",
    message: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      if (role === "caregiver" && includePlacement) {
        try {
          await api.post("/placement-requests", {
            ...placement,
            contact_name: u.name,
            contact_email: u.email,
            contact_phone: u.phone,
            referral_source: params.get("ref") || "",
          });
          toast.success(
            "Account created — a Providence Partners advisor will reach out shortly."
          );
        } catch (_) {
          toast.warning("Account created — but we couldn't queue your placement request. Try again from the dashboard.");
        }
      }
      if (u.role === "facility") navigate("/dashboard/facility");
      else navigate("/dashboard/caregiver");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      toast.error("Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="mx-auto max-w-2xl px-6 py-16">
      <Card className="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        <h1 className="font-serif text-3xl text-forest">Create your account</h1>
        <p className="mt-1 text-sm text-muted-foreground">It takes less than a minute.</p>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
          <button
            type="button"
            data-testid="role-caregiver-btn"
            onClick={() => setRole("caregiver")}
            className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
              role === "caregiver" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            <Heart className="h-4 w-4" /> Family seeking care
          </button>
          <button
            type="button"
            data-testid="role-facility-btn"
            onClick={() => setRole("facility")}
            className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
              role === "facility" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
            }`}
          >
            <Building2 className="h-4 w-4" /> I run a care home
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Full name</label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name-input"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email-input"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Phone</label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="register-phone-input"
                className="mt-1 h-11"
                placeholder="(248) 555-0123"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password-input"
                className="mt-1 h-11"
              />
            </div>
          </div>

          {role === "caregiver" && (
            <div className="rounded-2xl border border-gold/40 bg-gold/5 p-5" data-testid="placement-section">
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={includePlacement}
                  onCheckedChange={(v) => setIncludePlacement(!!v)}
                  data-testid="include-placement-checkbox"
                  className="mt-0.5"
                />
                <div>
                  <p className="font-serif text-lg text-forest">
                    <Sparkles className="mr-1 inline h-4 w-4 text-gold" />
                    Looking for a permanent home for Mom or Dad?
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Check this and a <span className="font-medium text-forest">Providence Partners advisor</span> will reach out personally to help with senior placement — memory care, assisted living, or a small group home. <span className="italic">Different from drop-in or trial visits to individual homes.</span>
                  </p>
                </div>
              </label>

              {includePlacement && (
                <div className="mt-5 grid gap-4 sm:grid-cols-2 border-t border-gold/30 pt-5">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Loved one's first name</label>
                    <Input
                      value={placement.loved_one_name}
                      onChange={(e) => setPlacement({ ...placement, loved_one_name: e.target.value })}
                      placeholder="e.g. Margaret"
                      data-testid="register-placement-loved-one"
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Care priority</label>
                    <select
                      value={placement.care_priority}
                      onChange={(e) => setPlacement({ ...placement, care_priority: e.target.value })}
                      data-testid="register-placement-care-priority"
                      className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    >
                      <option value="memory_care">Memory care / dementia</option>
                      <option value="parkinsons">Parkinson's</option>
                      <option value="mobility">Mobility / stroke</option>
                      <option value="general">General senior placement</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Preferred area (city or ZIP)</label>
                    <Input
                      value={placement.preferred_area}
                      onChange={(e) => setPlacement({ ...placement, preferred_area: e.target.value })}
                      placeholder="e.g. Troy, MI"
                      data-testid="register-placement-area"
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Timeline</label>
                    <select
                      value={placement.timeline}
                      onChange={(e) => setPlacement({ ...placement, timeline: e.target.value })}
                      data-testid="register-placement-timeline"
                      className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    >
                      <option value="urgent">Urgent — within 2 weeks</option>
                      <option value="1_3_months">1–3 months</option>
                      <option value="exploring">Just exploring</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">A few words about the situation</label>
                    <textarea
                      rows={3}
                      value={placement.situation}
                      onChange={(e) => setPlacement({ ...placement, situation: e.target.value })}
                      placeholder="Where Mom is now, what's prompting the search…"
                      data-testid="register-placement-situation"
                      className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-muted-foreground">How should we reach you first?</label>
                    <div className="mt-2 flex gap-2">
                      {[{ v: "phone", label: "Phone call" }, { v: "email", label: "Email" }].map((c) => (
                        <button
                          key={c.v}
                          type="button"
                          onClick={() => setPlacement({ ...placement, preferred_contact: c.v })}
                          data-testid={`register-placement-contact-${c.v}`}
                          className={`rounded-full px-4 py-2 text-sm transition-colors ${
                            placement.preferred_contact === c.v
                              ? "bg-primary text-primary-foreground"
                              : "border border-border bg-card text-foreground hover:bg-muted"
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
          <Button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full rounded-full bg-primary py-6 text-primary-foreground"
          >
            {loading
              ? "Creating…"
              : role === "caregiver" && includePlacement
              ? "Create account & request advisor"
              : "Create account"}
          </Button>
        </form>
        <p className="mt-5 text-sm text-muted-foreground">
          Already a member?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </Card>
    </div>
  );
}
